<template>
	<div v-if="resource.name" class="timelineResource mr-02 flex items-center bg-level-01">
		<div v-if="isParentResource && !flat" :class="['expandIcon', { expandIcon__rotated: resource.collapsed }]">
			<Icon name="chevron-down" size="1.5" class="cursor-pointer text-ink-38" @click="toggleCollapsed" />
		</div>

		<div
			:class="[
				'flex w-full items-center justify-between overflow-x-hidden',
				{ 'ml-06': !isParentResource && !flat, selectedBackground: isSelected && !isLoadingSKUData(String(resource.id)) }
			]"
			@mouseenter="onMouseEnter"
			@mouseleave="onMouseLeave"
		>
			<!-- Fine tuning by 1px to make sure that resource name is in the exact same place when
		 	showing a loader and when showing a checkbox -->
			<div
				v-if="(!isParentResource && isLoadingSKUData(String(resource.id))) || readonly"
				class="font-body-s ml-03 mt-[1px] flex h-07 items-center gap-03 text-ink-60"
			>
				<Loader v-if="!readonly" class="z-10" size="big" />

				<MobiscrollTooltipIfEllipsis :tooltip-text="resource.name" :max-width="resourceNameMaxLength">
					<span class="font-body-s resourceName">
						<!-- The `resourceName` CSS class is used as a selector elsewhere so please do not remove it -->
						{{ resource.name }}
					</span>
				</MobiscrollTooltipIfEllipsis>
			</div>

			<Checkbox
				v-else
				:model-value="isSelected"
				:value="true"
				:indeterminate="isPartiallySelected"
				:label="resource.name"
				:disabled="isReadonly || (isUpdateEvent && !doesSkuHaveEventRelatedToUpdatedEvent(String(resource.id)))"
				no-ellipsis-tooltip
				@update:model-value="toggleSelected"
			>
				<template #label-text>
					<MobiscrollTooltipIfEllipsis :tooltip-text="resource.name">
						<!-- The `resourceName` CSS class is used as a selector elsewhere so please do not remove it -->
						<span :class="['font-body-s resourceName', { 'font-body-s-medium uppercase text-ink-38': isParentResource }]">
							{{ resource.name }}
						</span>
					</MobiscrollTooltipIfEllipsis>
				</template>
			</Checkbox>

			<MobiscrollTooltip
				v-if="!isParentResource && !readonly && isHover && !isLoadingSKUData(String(resource.id))"
				:content="t('showHistoryTooltip')"
			>
				<IconButton class="showHistoryButton" size="xs" icon="calendar-clock-outline" @click.stop="showHistory()" />
			</MobiscrollTooltip>
		</div>
	</div>

	<div v-else-if="resource.id === SPECIAL_EVENT_TEMPLATE_ID" class="h-[2rem] bg-level-01"></div>
</template>

<script setup lang="ts">
import { SPECIAL_EVENT_TEMPLATE_ID, type StoreName } from '@bluebrick/discounts-service-module'
import { Checkbox, Icon, IconButton, Loader, useHover, useTranslation } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { computed, toRefs } from 'vue'
import MobiscrollTooltip from '@/common/components/MobiscrollTooltip.vue'
import MobiscrollTooltipIfEllipsis from '@/common/components/MobiscrollTooltipIfEllipsis.vue'
import { useEventDetailsBaseStore } from '@/features/eventDetails/useEventDetailsBaseStore'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import { useEventsStore } from '@/features/events/useEventsStore'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import type { ProductResource } from '@/types'

const props = defineProps<{
	resource: ProductResource
	store: StoreName
	readonly?: boolean
	flat?: boolean
}>()

const { store, resource, readonly, flat } = toRefs(props)

const { isHover, onMouseEnter, onMouseLeave } = useHover()

const { trackEvent } = useTelemetry()
const { showProductHistory } = useSidebarStore()
const { doesSkuHaveEventRelatedToUpdatedEvent } = useEventDetailsBaseStore()
const { isReadonly, isUpdateEvent } = storeToRefs(useEventDetailsStore())
const { isLoadingSKUData } = storeToRefs(useEventsStore())
const { isSKUSelected, isProductPartiallySelected, isProductSelected, toggleSKUSelection, selectSKU, unselectSKU } = useEventsStore()

const { t } = useTranslation()

const emit = defineEmits<{
	toggleCollapsed: [value: boolean]
}>()

const isParentResource = computed(() => !!resource.value.children && resource.value.children.length > 0)

const isSelected = computed(
	() =>
		(!!isParentResource.value && isProductSelected(store.value, String(resource.value.id))) ||
		(!isParentResource.value && isSKUSelected(resource.value.skuData?.unique_sku_id ?? resource.value.id))
)

const isPartiallySelected = computed(() => !!isParentResource.value && isProductPartiallySelected(store.value, String(resource.value.id)))

const resourceNameMaxLength = computed(() => {
	let width = 12

	if (readonly.value) {
		// In readonly mode we don't show the "show SKU history" icon
		width += 3
	}

	if (flat.value) {
		// In flat resources mode we don't show the space for expand/collapse product icon
		width += 2
	}

	return width
})

function toggleCollapsed() {
	emit('toggleCollapsed', !resource.value.collapsed)
}

function toggleSelected() {
	if (isParentResource.value) {
		// Product
		if (isSelected.value) {
			trackEvent('user', 'Deselected a product in timeline resource.', {
				store: store.value
			})

			resource.value.children?.forEach((sku) => {
				unselectSKU(sku.skuData.unique_sku_id)
			})

			return
		}

		if (isPartiallySelected.value) {
			trackEvent('user', 'Selected partial product in timeline resource.', {
				store: store.value
			})

			resource.value.children?.forEach((sku) => {
				selectSKU(sku.skuData.unique_sku_id)
			})

			return
		}

		trackEvent('user', 'Selected a product in timeline resource.', {
			store: store.value
		})

		resource.value.children?.forEach((sku) => {
			selectSKU(sku.skuData.unique_sku_id)
		})

		return
	}

	// SKU
	trackEvent('user', isSelected.value ? 'Unchecked a product in store.' : 'Checked a product in store.', {
		store: store.value
	})

	toggleSKUSelection(resource.value.skuData.unique_sku_id)
}

function showHistory() {
	trackEvent('user', 'Open history from timeline products list', {
		store: store.value
	})

	showProductHistory({
		storeId: store.value,
		uniqueSkuId: resource.value.skuData.unique_sku_id,
		humanName: resource.value.skuData.human_name,
		discountDepth: 0
	})
}
</script>

<style lang="scss" scoped>
.showHistoryButton {
	&:hover {
		:deep(svg) {
			fill: theme('colors.primary.100');
		}
	}
}

.expandIcon {
	transition: all 0.1s linear;

	&__rotated {
		transform: rotate(-90deg);
	}
}

.selectedBackground {
	background-color: rgb(from theme('colors.primary.100') r g b / 0.2);
}
</style>
